import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, FileText, Calendar, TrendingUp, Target, 
  CheckCircle, ChevronRight, X, Sparkles, Lightbulb,
  Bot, PenTool, HelpCircle, Zap, BookOpen, Settings,
  RefreshCw, ArrowRight, Info
} from 'lucide-react';

const WorkspaceWelcome = ({ onClose, onNavigate, competenceData, currentView = 'chat' }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  // Analyser l'état de l'objectif
  const hasNotes = competenceData?.stepNotes && Object.keys(competenceData.stepNotes).length > 0;
  const hasPlanning = competenceData?.weeklyPlanning && competenceData.weeklyPlanning.length > 0;
  const isFirstTime = !hasNotes && !hasPlanning;

  const slides = [
    {
      id: 'intro',
      title: 'Bienvenue dans votre Workspace Aladinn',
      subtitle: 'Votre espace de travail intelligent pour réussir votre projet',
      description: 'Découvrez comment Aladinn vous accompagne à chaque étape avec une assistance personnalisée et des outils puissants.',
      icon: Target,
      gradient: 'from-purple-500 to-indigo-600',
      features: [
        'Conversation assistée par IA',
        'Suivi automatique de progression',
        'Notes intelligentes synchronisées',
        'Planning et analytics'
      ]
    },
    {
      id: 'chat-modes',
      title: 'Deux modes de conversation',
      subtitle: 'Choisissez selon vos besoins',
      description: 'Le Workspace propose deux façons complémentaires de travailler avec Aladinn.',
      icon: MessageCircle,
      gradient: 'from-blue-500 to-cyan-600',
      modes: [
        {
          name: 'Conversation libre',
          icon: MessageCircle,
          color: 'blue',
          description: 'Discutez librement avec Aladinn de votre projet',
          useCases: [
            'Poser des questions spécifiques',
            'Explorer de nouvelles idées',
            'Obtenir des conseils personnalisés',
            'Sauvegarder manuellement des informations'
          ],
          badge: 'Flexible'
        },
        {
          name: 'Progression guidée',
          icon: Target,
          color: 'green',
          description: 'Suivez un parcours structuré question par question',
          useCases: [
            'Avancer étape par étape',
            'Répondre à des questions ciblées',
            'Mise à jour automatique des notes',
            'Validation de votre progression'
          ],
          badge: 'Structuré'
        }
      ]
    },
    {
      id: 'notes-sync',
      title: 'Notes intelligentes synchronisées',
      subtitle: 'Chat et Notes : deux vues, un seul système',
      description: 'Vos conversations alimentent automatiquement vos notes de projet.',
      icon: FileText,
      gradient: 'from-green-500 to-emerald-600',
      sections: [
        {
          name: 'Décisions et actions',
          icon: CheckCircle,
          color: 'green',
          description: 'Capture automatique de vos décisions et actions à entreprendre',
          auto: true
        },
        {
          name: 'Questions et défis',
          icon: HelpCircle,
          color: 'orange',
          description: 'Identification des points bloquants et défis à résoudre',
          auto: true
        },
        {
          name: 'Recommandations IA',
          icon: Sparkles,
          color: 'purple',
          description: 'Suggestions personnalisées basées sur votre contexte',
          auto: true
        },
        {
          name: 'Ressources',
          icon: BookOpen,
          color: 'blue',
          description: 'Sauvegarde manuelle des informations utiles par thème',
          auto: false
        }
      ]
    },
    {
      id: 'workspace-views',
      title: 'Quatre espaces complémentaires',
      subtitle: 'Naviguez selon vos besoins',
      description: 'Chaque onglet offre une vue différente sur votre projet.',
      icon: Settings,
      gradient: 'from-orange-500 to-red-600',
      views: [
        {
          id: 'chat',
          name: 'Chat',
          icon: MessageCircle,
          color: 'blue',
          description: 'Conversez avec Aladinn et guidez votre projet',
          tips: 'Utilisez le mode guidé pour une progression structurée'
        },
        {
          id: 'notes',
          name: 'Notes',
          icon: FileText,
          color: 'purple',
          description: 'Consultez et organisez vos notes de projet',
          tips: 'Alimentées automatiquement par vos conversations'
        },
        {
          id: 'planning',
          name: 'Planning',
          icon: Calendar,
          color: 'green',
          description: 'Organisez vos actions dans le temps',
          tips: 'Planification hebdomadaire et suivi quotidien'
        },
        {
          id: 'analytics',
          name: 'Progression',
          icon: TrendingUp,
          color: 'orange',
          description: 'Visualisez votre avancement et vos résultats',
          tips: 'Tableaux de bord et métriques de succès'
        }
      ]
    }
  ];

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;
  const Icon = currentSlideData.icon;

  const handleNext = () => {
    if (isLastSlide) {
      localStorage.setItem('aladinn_workspace_welcome_seen', 'true');
      onClose();
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('aladinn_workspace_welcome_seen', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header avec progression */}
        <div className={`relative bg-gradient-to-r ${currentSlideData.gradient} p-6 text-white`}>
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Icon size={28} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">
                {currentSlideData.title}
              </h2>
              <p className="text-white/90 text-sm">
                {currentSlideData.subtitle}
              </p>
            </div>
          </div>

          {/* Indicateurs de progression */}
          <div className="flex gap-2">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`h-1 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-white flex-1' 
                    : index < currentSlide
                    ? 'bg-white/60 w-8'
                    : 'bg-white/30 w-8'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Slide Introduction */}
          {currentSlide === 0 && (
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                {currentSlideData.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentSlideData.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200"
                  >
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              {isFirstTime && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">
                        Premier pas recommandé
                      </h3>
                      <p className="text-sm text-blue-800">
                        Commencez par le mode <strong>Progression guidée</strong> pour structurer 
                        votre approche, puis basculez en <strong>Conversation libre</strong> quand vous le souhaitez.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Slide Modes de conversation */}
          {currentSlide === 1 && (
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                {currentSlideData.description}
              </p>

              <div className="space-y-4">
                {currentSlideData.modes.map((mode, index) => (
                  <div
                    key={index}
                    className={`p-5 rounded-xl border-2 bg-gradient-to-br ${
                      index === 0 
                        ? 'border-blue-300 from-blue-50 to-blue-100' 
                        : 'border-green-300 from-green-50 to-green-100'
                    }`}
                  >
                    <div className="flex items-start gap-4 mb-3">
                      <div className={`w-12 h-12 rounded-lg bg-${mode.color}-200 flex items-center justify-center`}>
                        <mode.icon className={`w-6 h-6 text-${mode.color}-700`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-gray-900">
                            {mode.name}
                          </h3>
                          <span className={`text-xs px-2 py-1 bg-${mode.color}-600 text-white rounded-full`}>
                            {mode.badge}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm">{mode.description}</p>
                      </div>
                    </div>

                    <div className="space-y-2 ml-16">
                      {mode.useCases.map((useCase, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <ArrowRight className={`w-4 h-4 text-${mode.color}-600`} />
                          <span className="text-sm text-gray-700">{useCase}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-purple-900 mb-1">
                      Basculez librement entre les deux modes
                    </h3>
                    <p className="text-sm text-purple-800">
                      Utilisez les boutons en haut du Chat pour changer de mode à tout moment. 
                      Vos données sont synchronisées automatiquement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Slide Notes synchronisées */}
          {currentSlide === 2 && (
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                {currentSlideData.description}
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <MessageCircle className="w-6 h-6 text-blue-600" />
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-sm text-gray-800 font-medium">
                  <strong>Chat → Notes</strong> : Vos conversations mettent à jour automatiquement 
                  vos notes. Consultez l'onglet Notes pour voir le résultat.
                </p>
              </div>

              <div className="space-y-3">
                {currentSlideData.sections.map((section, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 bg-${section.color}-50 border-${section.color}-400`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-${section.color}-100 flex items-center justify-center flex-shrink-0`}>
                        <section.icon className={`w-5 h-5 text-${section.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">
                            {section.name}
                          </h3>
                          {section.auto && (
                            <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded-full flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              Auto
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700">
                          {section.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-orange-900 mb-1">
                      Sauvegarde manuelle de ressources
                    </h3>
                    <p className="text-sm text-orange-800">
                      Seule la section <strong>Ressources</strong> nécessite une action manuelle : 
                      cliquez sur l'icône de sauvegarde dans le chat pour classer une information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Slide Espaces du Workspace */}
          {currentSlide === 3 && (
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                {currentSlideData.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentSlideData.views.map((view, index) => (
                  <button
                    key={view.id}
                    onClick={() => {
                      onNavigate(view.id);
                      handleSkip();
                    }}
                    className={`p-4 rounded-xl border-2 transition-all hover:shadow-lg text-left ${
                      view.id === currentView
                        ? `border-${view.color}-400 bg-${view.color}-50`
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg bg-${view.color}-100 flex items-center justify-center`}>
                        <view.icon className={`w-5 h-5 text-${view.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {view.name}
                        </h3>
                        <p className="text-sm text-gray-700">
                          {view.description}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </div>
                    
                    <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-200">
                      <Lightbulb className={`w-4 h-4 text-${view.color}-500 flex-shrink-0 mt-0.5`} />
                      <p className="text-xs text-gray-600">
                        {view.tips}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-purple-900 mb-1">
                      Tout est synchronisé en temps réel
                    </h3>
                    <p className="text-sm text-purple-800">
                      Naviguez librement entre les onglets : vos modifications sont 
                      automatiquement sauvegardées et disponibles partout.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer avec navigation */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Passer
            </button>

            <div className="flex items-center gap-3">
              {currentSlide > 0 && (
                <button
                  onClick={() => setCurrentSlide(prev => prev - 1)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Retour
                </button>
              )}
              
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <span className="font-medium">
                  {isLastSlide ? 'Commencer' : 'Suivant'}
                </span>
                {isLastSlide ? <Sparkles size={18} /> : <ChevronRight size={18} />}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default WorkspaceWelcome;